import { z } from 'zod';

import { UnityArticle } from '../content/schemas/article';
import { Metadata } from '../metadata/schemas';

export const ResultChoice = z.object({
    createdAt: z.string().nullable(),
    feedbackEvaluation: z.enum(['THUMBS_UP', 'THUMBS_DOWN']).nullable(),
    id: z.string().nullable(),
    interactionId: z.string().nullable(),
    used: z.boolean(),
});
export type ResultChoice = z.infer<typeof ResultChoice>;

export const TeaserGenerator = z.object({
    title: z.string(),
    titleHeader: z.string(),
    lead: z.string(),
});

export type TeaserGenerator = z.infer<typeof TeaserGenerator>;

export const TeaserGeneration = z.object({
    createdAt: z.string(),
    resourceId: z.string(),
    resourceName: z.string(),
    interactionId: z.string(),
    interactionResults: z.array(
        z.object({
            teasers: z.array(TeaserGenerator),
        }),
    ),
});

export type TeaserGeneration = z.infer<typeof TeaserGeneration>;

export const KeywordSuggestions = ResultChoice.extend({
    keywords: z.array(z.string()),
    articleId: z.string().nullable(),
});

export type KeywordSuggestions = z.infer<typeof KeywordSuggestions>;

export const KeywordsAndSeoTeaser = z.object({
    articleId: z.string(),
    keywords: z.array(z.string()),
    seoTeaser: z.object({
        title: z.string(),
        lead: z.string(),
    }),
});

export type KeywordsAndSeoTeaser = z.infer<typeof KeywordsAndSeoTeaser>;

export const SeoTeaser = z.object({
    articleId: z.string(),
    title: z.string(),
    lead: z.string(),
});

export type SeoTeaser = z.infer<typeof SeoTeaser>;

export const SpecialTeaserSuggestionResult = z.object({
    title: z.string(),
    lead: z.string(),
});

export type SpecialTeaserSuggestionResult = z.infer<typeof SpecialTeaserSuggestionResult>;

export const SpecialTeaserSuggestion = z.object({
    resourceId: z.string(),
    resourceName: z.string(),
    interactionId: z.string(),
    createdAt: z.string(),
    interactionResults: z.tuple([SpecialTeaserSuggestionResult]),
});

export type SpecialTeaserSuggestion = z.infer<typeof SpecialTeaserSuggestion>;

export const SpecialTeaserSuggestionPostBody = z.object({
    metadataId: z.number(),
});

export type SpecialTeaserSuggestionPostBody = z.infer<typeof SpecialTeaserSuggestionPostBody>;

export const ImageAltText = ResultChoice.extend({
    altText: z.string(),
    id: z.string(),
});

export type ImageAltText = z.infer<typeof ImageAltText>;

export const TickerSummarySuggestionResult = z.object({
    items: z.array(z.string()),
});

export type TickerSummarySuggestionResult = z.infer<typeof TickerSummarySuggestionResult>;

export const TickerSummary = z.object({
    createdAt: z.string(),
    resourceId: z.string(),
    resourceName: z.string(),
    interactionId: z.string(),
    interactionResults: z.array(TickerSummarySuggestionResult),
});

export type TickerSummary = z.infer<typeof TickerSummary>;

export const ArticleSummary = z.object({
    createdAt: z.string(),
    resourceId: z.string(),
    resourceName: z.string(),
    interactionId: z.string(),
    interactionResults: z.array(
        z.object({
            items: z.array(z.string()),
        }),
    ),
});

export type ArticleSummary = z.infer<typeof ArticleSummary>;

export const ArticleRephrasing = ResultChoice.extend({
    rephrasedText: z.string(),
    sourceText: z.string(),
    explanation: z.string(),
    articleId: UnityArticle.shape.id,
});

export type ArticleRephrasing = z.infer<typeof ArticleRephrasing>;

export const articleBuddyCategories = {
    STANDARDS: [
        'CONTEXT_AND_ACCURACY',
        'SOURCE_TRANSPARENCY',
        'FAIRNESS',
        'OPINION_VS_FACT',
        'INDEPENDENCE',
        'PLURALISM',
        'BALANCED_REPORTING',
        'NON_DISCRIMINATION',
        'PRIVACY',
    ],
    STYLE: [
        'LANGUAGE_STYLE',
        'SENTENCE_STRUCTURE',
        'QUOTATIONS',
        'PUNCTUATION',
        'HELVETISMS',
        'CLARITY',
        'GENDER_INCLUSIVE_LANGUAGE',
        'STRUCTURE_NARRATIVE_ARC',
        'TONE_LANGUAGE_REGISTER',
    ],
    SHORTENING: ['ERASE', 'PARAPHRASE'],
    NAME_CHECKER: ['MISSPELLED_NAMES'],
    GRAPHIC: ['CHART_POTENTIAL'],
    FACT_CHECK: ['SUPPORTING_SOURCES', 'MISSING_SOURCES', 'CONFLICTING_SOURCES'],
} as const;

const ArticleBuddyAnyCategory = z.enum([
    ...articleBuddyCategories.STANDARDS,
    ...articleBuddyCategories.STYLE,
    ...articleBuddyCategories.SHORTENING,
    ...articleBuddyCategories.NAME_CHECKER,
    ...articleBuddyCategories.GRAPHIC,
    ...articleBuddyCategories.FACT_CHECK,
]);

export const ArticleBuddySuggestion = z.object({
    id: z.string(),
    find: z.string(),
    highlight: z.string(),
    reason: z.string(),
    replaceValue: z.string(),
    category: ArticleBuddyAnyCategory,
});
export type ArticleBuddySuggestion = z.infer<typeof ArticleBuddySuggestion>;

// These checks only ask the author to verify a passage, so approving must not touch the text
const verifyOnlyCategories: readonly ArticleBuddySuggestion['category'][] = [
    ...articleBuddyCategories.GRAPHIC,
    ...articleBuddyCategories.FACT_CHECK,
];

// Any check can ask to verify a passage without changing it by returning the highlight unchanged
export const isTextReplacingSuggestion = (suggestion: ArticleBuddySuggestion) =>
    !verifyOnlyCategories.includes(suggestion.category) &&
    suggestion.highlight !== suggestion.replaceValue;

export const articleBuddyAnalysisTypes = [
    'STANDARDS',
    'STYLE',
    'NAME_CHECKER',
    'GRAPHIC',
    'FACT_CHECK',
    'SHORTENING',
] as const;
export const ArticleBuddyAnalysisType = z.enum(articleBuddyAnalysisTypes);
export type ArticleBuddyAnalysisType = z.infer<typeof ArticleBuddyAnalysisType>;

const ArticleBuddyInteractionResultBase = z.object({
    summary: z.string(),
});

const ArticleBuddyInteractionResult = z.discriminatedUnion('analysisType', [
    z.object({
        ...ArticleBuddyInteractionResultBase.shape,
        analysisType: z.literal('STANDARDS'),
        suggestions: z.array(ArticleBuddySuggestion),
    }),
    z.object({
        ...ArticleBuddyInteractionResultBase.shape,
        analysisType: z.literal('STYLE'),
        suggestions: z.array(ArticleBuddySuggestion),
    }),
    z.object({
        ...ArticleBuddyInteractionResultBase.shape,
        analysisType: z.literal('SHORTENING'),
        suggestions: z.array(ArticleBuddySuggestion),
    }),
    z.object({
        ...ArticleBuddyInteractionResultBase.shape,
        analysisType: z.literal('NAME_CHECKER'),
        suggestions: z.array(ArticleBuddySuggestion),
    }),
    z.object({
        ...ArticleBuddyInteractionResultBase.shape,
        analysisType: z.literal('GRAPHIC'),
        suggestions: z.array(ArticleBuddySuggestion),
    }),
    z.object({
        ...ArticleBuddyInteractionResultBase.shape,
        analysisType: z.literal('FACT_CHECK'),
        suggestions: z.array(ArticleBuddySuggestion),
    }),
]);

type ArticleBuddyInteractionResult = z.infer<typeof ArticleBuddyInteractionResult>;

export const ArticleBuddy = z.object({
    resourceId: z.string(),
    resourceName: z.string(),
    interactionId: z.string(),
    createdAt: z.string(),
    interactionResults: z.array(ArticleBuddyInteractionResult),
});
export type ArticleBuddy = z.infer<typeof ArticleBuddy>;

const InteractionStatus = z.enum(['PENDING', 'SUCCEEDED', 'FAILED', 'TIMED_OUT', 'CANCELLED']);
type InteractionStatus = z.infer<typeof InteractionStatus>;

export const isFailedInteractionStatus = (status: InteractionStatus | undefined) =>
    status === 'FAILED' || status === 'TIMED_OUT' || status === 'CANCELLED';

export const ArticleBuddyInteractionStatus = z.object({
    analysisType: ArticleBuddyAnalysisType,
    status: InteractionStatus,
});

export const ArticleBuddyPostBody = z.object({
    metadataId: Metadata.shape.id,
    analysisTypes: z.array(ArticleBuddyAnalysisType),
    targetTextLength: z.number().nullish(),
});
export type ArticleBuddyPostBody = z.infer<typeof ArticleBuddyPostBody>;

export type ArticleBuddyInteractionStatus = z.infer<typeof ArticleBuddyInteractionStatus>;

export const ArticleBuddyStatuses = z.object({
    resourceId: z.string(),
    resourceName: z.string(),
    interactionId: z.string(),
    createdAt: z.string(),
    interactionStatuses: z.array(ArticleBuddyInteractionStatus),
});

export type ArticleBuddyStatuses = z.infer<typeof ArticleBuddyStatuses>;

const ArticleTranslationStatus = z.object({
    status: InteractionStatus,
});

export const ArticleTranslationStatuses = z.object({
    resourceId: z.string(),
    resourceName: z.string(),
    interactionId: z.string(),
    createdAt: z.string(),
    interactionStatuses: z.array(ArticleTranslationStatus),
});
export type ArticleTranslationStatuses = z.infer<typeof ArticleTranslationStatuses>;

export const ArticleTranslation = z.object({
    resourceId: z.string(),
    resourceName: z.string(),
    interactionId: z.string(),
    createdAt: z.string(),
    interactionResults: z.array(
        z.object({
            translatedArticleId: z.string(),
        }),
    ),
});
export type ArticleTranslation = z.infer<typeof ArticleTranslation>;
